import { PRIORITY_ASSAY_IDS } from './constants';

/**
 * Filter items by selected tags
 * @param {Array} items - Items to filter
 * @param {Array} selectedTags - Selected tags array with {name} objects
 * @returns {Array} Filtered items
 */
export function filterByTags(items, selectedTags) {
  if (!items) return [];
  if (!selectedTags || selectedTags.length === 0) return items;

  const tagNames = selectedTags.map(tag => tag.name);
  return items.filter(item =>
    item.tag && tagNames.some(tagName => item.tag.includes(tagName))
  );
}

/**
 * Filter items by search term using provided compare function
 * @param {Array} items - Items to filter
 * @param {string} searchTerm - Search term
 * @param {Function} compareFunc - Compare function (item, term) => boolean
 * @returns {Array} Filtered items
 */
export function filterBySearchTerm(items, searchTerm, compareFunc) {
  if (!items) return [];
  if (!searchTerm || searchTerm.trim().length === 0) return items;

  const normalizedTerm = searchTerm.trim().toLowerCase();
  return items.filter(item => compareFunc(item, normalizedTerm));
}

/**
 * Check if item is a priority assay (single filling, not complex, or specific IDs)
 * @param {Object} item - Item to check
 * @returns {boolean}
 */
function isPriorityAssay(item) {
  const hasSpecialId = PRIORITY_ASSAY_IDS.includes(+item.id);
  const isSingleFilling = item.filling && item.filling.length === 1;
  const isNotComplex = item.opt && !item.opt.includes('complex');

  return hasSpecialId || (isSingleFilling && isNotComplex);
}

/**
 * Sort search results with priority:
 * 1. Items starting with search term
 * 2. Complex items with numeric sorting
 * 3. Items containing search term (by position)
 * 4. Priority assays before others
 * 5. Alphabetical order
 *
 * @param {Array} items - Items to sort
 * @param {string} searchTerm - Search term for relevance sorting
 * @returns {Array} Sorted items
 */
export function sortSearchResults(items, searchTerm) {
  if (!items || items.length === 0) return [];
  if (!searchTerm || searchTerm.trim().length === 0) return items;

  const normalizedTerm = searchTerm.trim().toLowerCase();

  return [...items].sort((a, b) => {
    const aName = a.name?.trim().toLowerCase() || '';
    const bName = b.name?.trim().toLowerCase() || '';

    // 1. Items starting with search term come first
    const aStartsWith = aName.indexOf(normalizedTerm) === 0;
    const bStartsWith = bName.indexOf(normalizedTerm) === 0;

    if (aStartsWith && !bStartsWith) return -1;
    if (bStartsWith && !aStartsWith) return 1;

    // 2. Both complex - sort numerically
    if (a.type === 'complex' && b.type === 'complex') {
      const aNum = a.name.replace(/[^0-9]/g, '');
      const bNum = b.name.replace(/[^0-9]/g, '');

      if (aNum && bNum) {
        return aNum.localeCompare(bNum, 'uk', { numeric: true });
      }
    }

    // 3. Sort by position of search term in name
    const aIndex = aName.indexOf(normalizedTerm);
    const bIndex = bName.indexOf(normalizedTerm);

    if (aIndex > 0 || bIndex > 0) {
      if (aIndex !== bIndex) {
        return bIndex - aIndex; // Earlier position = higher priority
      }
    }

    // 4. Priority assays before non-priority
    const aIsPriority = isPriorityAssay(a);
    const bIsPriority = isPriorityAssay(b);

    if (aIsPriority && !bIsPriority) return -1;
    if (bIsPriority && !aIsPriority) return 1;

    // 5. Alphabetical fallback
    return a.name.localeCompare(b.name);
  });
}

/**
 * Apply full filter pipeline: search + tags
 * @param {Array} items - Items to filter
 * @param {string} searchTerm - Search term
 * @param {Array} selectedTags - Selected tags
 * @param {Function} compareFunc - Compare function for search
 * @returns {Array} Filtered and sorted items
 */
export function applyFilters(items, searchTerm, selectedTags, compareFunc) {
  if (!items) return [];

  const normalizedTerm = searchTerm?.trim() || '';

  // If no search term, just filter by tags
  if (normalizedTerm.length === 0) {
    return filterByTags(items, selectedTags);
  }

  // Apply search filter
  let result = filterBySearchTerm(items, searchTerm, compareFunc);

  // Sort by relevance
  result = sortSearchResults(result, searchTerm);

  // Apply tag filter
  result = filterByTags(result, selectedTags);

  return result;
}
